import React from 'react';
import { Card, Stack, Typography } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import dayjs from 'dayjs';
import Yup from '../../../utils/yup';
import { ButtonUI, InputUI, DatePickerUI, AutoCompleteSelectUI, FormLabelUI, FormErrorUI } from '../../Interface';
import useUsers from '../../../context/UsersContext';

const EmployeeAbsenceRequestForm = ({ onSubmit, autocompleteOptions, autocompleteData }) => {
  const { user } = useUsers();

  const currentUser = autocompleteData?.user?.find((item) => item.userId === user.userId);

  const initialValues = {
    startDate: dayjs(),
    endDate: dayjs(),
    employeeId: currentUser?.employeeId ?? null,
    approverId: null,
    description: '',
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    employeeId: Yup.string().required(),
    approverId: Yup.string().required(),
    description: Yup.string().max(500),
  });

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, errors, setFieldValue, validateField }) => {
        const days = Math.max(
          dayjs(values.endDate).startOf('day').diff(dayjs(values.startDate).startOf('day'), 'day') + 1,
          0
        );

        return (
          <Form>
            <Card sx={{ py: 2.5, px: 1, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Field
                  name="employeeId"
                  component={AutoCompleteSelectUI}
                  options={autocompleteOptions.employee}
                  label={<FormLabelUI text="Dolgozó" />}
                  helperText={<FormErrorUI message={errors.employeeId} />}
                  disabled
                  variant="outlined"
                  size="small"
                  selectedValue={values.employeeId}
                />

                <Field
                  name="approverId"
                  component={AutoCompleteSelectUI}
                  options={autocompleteOptions.user}
                  label={<FormLabelUI text="Engedélyező" />}
                  helperText={<FormErrorUI message={errors.approverId} />}
                  variant="outlined"
                  size="small"
                  selectedValue={values.approverId}
                  onChange={(_e, newVal) => {
                    setFieldValue('approverId', newVal?.value ?? null).then(() => {
                      validateField('approverId');
                    });
                  }}
                />

                <Field
                  name="startDate"
                  component={DatePickerUI}
                  label={<FormLabelUI text="Kezdő dátum" />}
                  helperText={<FormErrorUI message={errors.startDate} />}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={values.startDate}
                  clearable={false}
                  onChange={(date) => {
                    setFieldValue('startDate', date).then(() => {
                      validateField('startDate');
                    });
                  }}
                />

                <Field
                  name="endDate"
                  component={DatePickerUI}
                  label={<FormLabelUI text="Befejező dátum" />}
                  helperText={<FormErrorUI message={errors.endDate} />}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={values.endDate}
                  clearable={false}
                  onChange={(date) => {
                    setFieldValue('endDate', date).then(() => {
                      validateField('endDate');
                    });
                  }}
                />

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    p: 1.5,
                    bgcolor: '#EAF5FF',
                    borderRadius: 2,
                  }}
                >
                  <CalendarMonth sx={{ color: '#315575' }} />
                  <Typography fontSize={14}>
                    Időtartam: <strong>{days} nap</strong>
                  </Typography>
                </Stack>

                <Stack spacing={0.5}>
                  <Field
                    type="text"
                    name="description"
                    as={InputUI}
                    fullWidth
                    multiline
                    rows={4}
                    label={<FormLabelUI text="Megjegyzés" />}
                    helperText={<FormErrorUI message={errors.description} />}
                    inputProps={{ maxLength: 500 }}
                    variant="outlined"
                    size="small"
                  />

                  <Typography textAlign="right" color="text.secondary" fontSize={12}>
                    {values.description?.length ?? 0} / 500
                  </Typography>
                </Stack>

                <ButtonUI text="Igénylés beküldése" type="submit" className="bg-success" />
              </Stack>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmployeeAbsenceRequestForm;
